import { useMemo } from "react";
import { useAtomValue } from "jotai";
import { dynamicNodeConfigsAtom } from "@/store/editor-v2";
import type { NodeDefinition } from "@/generated";

/**
 * Merge a node's dynamic-config override on top of its registry entry.
 * Per-connector fields from the override (visible, keys, label, …) replace
 * the registry values (matched by `name`) so canvas handles and chips reflect
 * what the backend's config_endpoint decided. Non-dynamic metadata
 * (data_type, rules, etc.) is preserved from the registry.
 */
export function useEffectiveRegEntry(
  nodeId: string | undefined,
  regEntry: NodeDefinition | undefined
): NodeDefinition | undefined {
  const overrides = useAtomValue(dynamicNodeConfigsAtom);

  return useMemo(() => {
    if (!regEntry || !nodeId) return regEntry;
    const override = overrides.get(nodeId);
    if (!override) return regEntry;

    const merge = (
      registryList: Array<unknown> | undefined,
      overrideList: Array<unknown> | undefined
    ): Array<unknown> | undefined => {
      if (!overrideList) return registryList;
      const registryByName = new Map<string, unknown>();
      for (const c of registryList ?? []) {
        const name = (c as { name?: string } | null)?.name;
        if (name) registryByName.set(name, c);
      }
      return overrideList.map((o) => {
        const name = (o as { name?: string } | null)?.name;
        const base = name ? registryByName.get(name) : undefined;
        return base
          ? {
              ...(base as Record<string, unknown>),
              ...(o as Record<string, unknown>),
            }
          : o;
      });
    };

    return {
      ...regEntry,
      inputs: merge(regEntry.inputs, override.inputs) ?? regEntry.inputs,
      outputs: merge(regEntry.outputs, override.outputs) ?? regEntry.outputs,
    };
  }, [regEntry, overrides, nodeId]);
}
